import { useCallback, useEffect, useMemo, useState } from "react";
import {
  Calendar,
  CalendarDays,
  ClipboardList,
  Clock,
  UserX,
} from "lucide-react";
import API from "../../services/api";
import { useToast } from "../Context/ToastContext";
import { useEmployees } from "../../hooks/useEmployees";
import { employeeFullName } from "./employeeReportsUtils";
import {
  buildAbsentReportRows,
  buildAttendanceRegisterRows,
  buildDailyAttendanceRows,
  buildEmployeeDeptMap,
  buildLateReportRows,
  buildMonthlySummaryRows,
  currentYearMonth,
  exportRowsToXlsx,
  exportRowsToPdf,
  registerAllColumns,
  toIsoDate,
} from "./attendanceReportsUtils";
import ReportPageHeader from "./components/ReportPageHeader";
import ReportLibraryHeader from "./components/ReportLibraryHeader";
import ReportCard from "./components/ReportCard";
import ReportActions from "./components/ReportActions";
import ReportViewModal from "./components/ReportViewModal";
import ReportFilterField from "./components/ReportFilterField";
import "../../styles/employeeReports.css";
import "../../styles/attendanceReports.css";

const MONTH_OPTIONS = [
  { value: 1, label: "January" },
  { value: 2, label: "February" },
  { value: 3, label: "March" },
  { value: 4, label: "April" },
  { value: 5, label: "May" },
  { value: 6, label: "June" },
  { value: 7, label: "July" },
  { value: 8, label: "August" },
  { value: 9, label: "September" },
  { value: 10, label: "October" },
  { value: 11, label: "November" },
  { value: 12, label: "December" },
];

function RegisterLegend({ inline = false }) {
  return (
    <div
      className={`ar-legend${inline ? " ar-legend--inline" : ""}`}
      aria-label="Attendance register legend"
    >
      <span><strong>P</strong> = Present</span>
      <span><strong>A</strong> = Absent</span>
      <span><strong>L</strong> = Leave</span>
      <span><strong>H</strong> = Holiday</span>
    </div>
  );
}

const DAILY_COLS = ["Employee", "Status", "In Time", "Out Time"];
const MONTHLY_COLS = ["Employee", "Present", "Absent", "Leave", "Holiday"];
const ABSENT_COLS = ["Employee", "Absent Date"];
const LATE_COLS = ["Employee", "Date", "Actual In Time", "Late By"];

export default function AttendanceReportsPage() {
  const toast = useToast();
  const { employees: employeeOptions } = useEmployees();
  const { year: defaultYear, month: defaultMonth } = useMemo(() => currentYearMonth(), []);
  const today = useMemo(() => toIsoDate(new Date()), []);
  const monthStart = useMemo(() => {
    const d = new Date();
    return toIsoDate(new Date(d.getFullYear(), d.getMonth(), 1));
  }, []);

  const [departments, setDepartments] = useState([]);
  const [employeeDeptMap, setEmployeeDeptMap] = useState({});
  const [busy, setBusy] = useState("");
  const [viewModal, setViewModal] = useState(null);

  const [dailyFilters, setDailyFilters] = useState({
    date: today,
    departmentId: "",
    employeeId: "",
  });

  const [monthlyFilters, setMonthlyFilters] = useState({
    month: defaultMonth,
    year: defaultYear,
    departmentId: "",
  });

  const [absentFilters, setAbsentFilters] = useState({
    fromDate: monthStart,
    toDate: today,
    departmentId: "",
  });

  const [lateFilters, setLateFilters] = useState({
    fromDate: monthStart,
    toDate: today,
    departmentId: "",
  });

  const [registerFilters, setRegisterFilters] = useState({
    month: defaultMonth,
    year: defaultYear,
    departmentId: "",
  });

  const yearOptions = useMemo(() => {
    const y = new Date().getFullYear();
    return [y - 2, y - 1, y, y + 1];
  }, []);

  useEffect(() => {
    const token = localStorage.getItem("token");
    const headers = token ? { Authorization: `Bearer ${token}` } : undefined;
    API.get("/setup/departments", { headers })
      .then((res) => setDepartments(res.data || []))
      .catch(() => setDepartments([]));
  }, []);

  useEffect(() => {
    setEmployeeDeptMap(buildEmployeeDeptMap(employeeOptions));
  }, [employeeOptions]);

  const loadDailyRows = useCallback(
    () =>
      buildDailyAttendanceRows({
        date: dailyFilters.date,
        departmentId: dailyFilters.departmentId,
        employeeId: dailyFilters.employeeId,
      }),
    [dailyFilters]
  );

  const loadMonthlyRows = useCallback(
    () =>
      buildMonthlySummaryRows({
        year: monthlyFilters.year,
        month: monthlyFilters.month,
        departmentId: monthlyFilters.departmentId,
      }),
    [monthlyFilters]
  );

  const loadAbsentRows = useCallback(
    () =>
      buildAbsentReportRows({
        fromDate: absentFilters.fromDate,
        toDate: absentFilters.toDate,
        departmentId: absentFilters.departmentId,
        employeeDeptMap,
      }),
    [absentFilters, employeeDeptMap]
  );

  const loadLateRows = useCallback(
    () =>
      buildLateReportRows({
        fromDate: lateFilters.fromDate,
        toDate: lateFilters.toDate,
        departmentId: lateFilters.departmentId,
        employeeDeptMap,
      }),
    [lateFilters, employeeDeptMap]
  );

  const loadRegisterFull = useCallback(
    () =>
      buildAttendanceRegisterRows({
        year: registerFilters.year,
        month: registerFilters.month,
        departmentId: registerFilters.departmentId,
      }),
    [registerFilters]
  );

  const registerFullCols = useMemo(
    () => registerAllColumns(registerFilters.year, registerFilters.month),
    [registerFilters.year, registerFilters.month]
  );

  const runAction = async (key, loader, filename, sheetName, viewTitle, viewColumns) => {
    try {
      setBusy(key);
      const rows = await loader();
      if (key.endsWith("view")) {
        const columns = viewColumns || (rows.length ? Object.keys(rows[0]) : []);
        setViewModal({
          title: viewTitle,
          columns,
          rows,
          showLegend: viewTitle === "Attendance Register",
        });
        return;
      }
      if (key.endsWith("pdf")) {
        exportRowsToPdf(rows, viewTitle || sheetName, filename, {
          legend: viewTitle === "Attendance Register",
        });
        toast.success("PDF export downloaded.");
        return;
      }
      exportRowsToXlsx(rows, sheetName, filename);
      toast.success("Excel export downloaded.");
    } catch {
      toast.error("Failed to generate report. Please try again.");
    } finally {
      setBusy("");
    }
  };

  const monthYearSelects = (filters, setFilters, idPrefix) => (
    <>
      <ReportFilterField id={`${idPrefix}-month`} label="Month">
        <select
          id={`${idPrefix}-month`}
          value={filters.month}
          onChange={(e) => setFilters((f) => ({ ...f, month: Number(e.target.value) }))}
        >
          {MONTH_OPTIONS.map((opt) => (
            <option key={opt.value} value={opt.value}>
              {opt.label}
            </option>
          ))}
        </select>
      </ReportFilterField>
      <ReportFilterField id={`${idPrefix}-year`} label="Year">
        <select
          id={`${idPrefix}-year`}
          value={filters.year}
          onChange={(e) => setFilters((f) => ({ ...f, year: Number(e.target.value) }))}
        >
          {yearOptions.map((y) => (
            <option key={y} value={y}>
              {y}
            </option>
          ))}
        </select>
      </ReportFilterField>
    </>
  );

  const departmentSelect = (filters, setFilters, id) => (
    <ReportFilterField id={id} label="Department">
      <select
        id={id}
        value={filters.departmentId}
        onChange={(e) => setFilters((f) => ({ ...f, departmentId: e.target.value }))}
      >
        <option value="">All Departments</option>
        {departments.map((d) => (
          <option key={d.id} value={d.id}>
            {d.name}
          </option>
        ))}
      </select>
    </ReportFilterField>
  );

  return (
    <div className="rpt-page ar-page rpt-page--enterprise">
      <ReportPageHeader
        icon={ClipboardList}
        category="Reports · Attendance"
        title="Attendance Reports"
        subtitle="Attendance analysis and payroll validation."
        breadcrumbCurrent="Attendance Reports"
      />

      <ReportLibraryHeader
        variant="enterprise"
        subtitle="Filter, preview, and export attendance reports"
        hint="Select filters, then click View to preview results."
      />

      <div className="rpt-report-list">
        <ReportCard
          layout="enterprise"
          reportType="daily-attendance"
          icon={CalendarDays}
          iconTone="blue"
          title="Daily Attendance Report"
          description="Daily attendance details for selected employees."
          filters={
            <>
              <ReportFilterField id="ar-daily-date" label="Date">
                <input
                  id="ar-daily-date"
                  type="date"
                  value={dailyFilters.date}
                  onChange={(e) => setDailyFilters((f) => ({ ...f, date: e.target.value || today }))}
                />
              </ReportFilterField>
              {departmentSelect(dailyFilters, setDailyFilters, "ar-daily-dept")}
              <ReportFilterField id="ar-daily-emp" label="Employee">
                <select
                  id="ar-daily-emp"
                  value={dailyFilters.employeeId}
                  onChange={(e) => setDailyFilters((f) => ({ ...f, employeeId: e.target.value }))}
                >
                  <option value="">All Employees</option>
                  {employeeOptions.map((emp) => (
                    <option key={emp.employee_id} value={emp.employee_id}>
                      {employeeFullName(emp)}
                      {emp.employee_code ? ` (${emp.employee_code})` : ""}
                    </option>
                  ))}
                </select>
              </ReportFilterField>
            </>
          }
          actions={
            <ReportActions
              variant="enterprise"
              busy={busy}
              onView={() => runAction("daily-view", loadDailyRows, "", "", "Daily Attendance Report", DAILY_COLS)}
              onExcel={() =>
                runAction("daily-excel", loadDailyRows, "daily_attendance_report.xlsx", "Daily Attendance", "")
              }
              onPdf={() =>
                runAction(
                  "daily-pdf",
                  loadDailyRows,
                  "daily_attendance_report.pdf",
                  "",
                  "Daily Attendance Report",
                  DAILY_COLS
                )
              }
            />
          }
        />

        <ReportCard
          layout="enterprise"
          reportType="monthly-summary"
          icon={Calendar}
          iconTone="green"
          title="Monthly Attendance Summary"
          description="Monthly attendance summary by department."
          filters={
            <>
              {monthYearSelects(monthlyFilters, setMonthlyFilters, "ar-monthly")}
              {departmentSelect(monthlyFilters, setMonthlyFilters, "ar-monthly-dept")}
            </>
          }
          actions={
            <ReportActions
              variant="enterprise"
              busy={busy}
              onView={() =>
                runAction("monthly-view", loadMonthlyRows, "", "", "Monthly Attendance Summary", MONTHLY_COLS)
              }
              onExcel={() =>
                runAction("monthly-excel", loadMonthlyRows, "monthly_attendance_summary.xlsx", "Monthly Summary", "")
              }
              onPdf={() =>
                runAction(
                  "monthly-pdf",
                  loadMonthlyRows,
                  "monthly_attendance_summary.pdf",
                  "",
                  "Monthly Attendance Summary",
                  MONTHLY_COLS
                )
              }
            />
          }
        />

        <ReportCard
          layout="enterprise"
          reportType="absent"
          icon={UserX}
          iconTone="orange"
          title="Absent Report"
          description="Employees marked absent in the selected period."
          filters={
            <>
              <ReportFilterField id="ar-absent-from" label="From Date">
                <input
                  id="ar-absent-from"
                  type="date"
                  value={absentFilters.fromDate}
                  onChange={(e) => setAbsentFilters((f) => ({ ...f, fromDate: e.target.value || f.fromDate }))}
                />
              </ReportFilterField>
              <ReportFilterField id="ar-absent-to" label="To Date">
                <input
                  id="ar-absent-to"
                  type="date"
                  value={absentFilters.toDate}
                  onChange={(e) => setAbsentFilters((f) => ({ ...f, toDate: e.target.value || f.toDate }))}
                />
              </ReportFilterField>
              {departmentSelect(absentFilters, setAbsentFilters, "ar-absent-dept")}
            </>
          }
          actions={
            <ReportActions
              variant="enterprise"
              busy={busy}
              showPdf={false}
              onView={() => runAction("absent-view", loadAbsentRows, "", "", "Absent Report", ABSENT_COLS)}
              onExcel={() => runAction("absent-excel", loadAbsentRows, "absent_report.xlsx", "Absent Report", "")}
            />
          }
        />

        <ReportCard
          layout="enterprise"
          reportType="late-coming"
          icon={Clock}
          iconTone="purple"
          title="Late Coming Report"
          description="Employees arriving late during the selected period."
          filters={
            <>
              <ReportFilterField id="ar-late-from" label="From Date">
                <input
                  id="ar-late-from"
                  type="date"
                  value={lateFilters.fromDate}
                  onChange={(e) => setLateFilters((f) => ({ ...f, fromDate: e.target.value || f.fromDate }))}
                />
              </ReportFilterField>
              <ReportFilterField id="ar-late-to" label="To Date">
                <input
                  id="ar-late-to"
                  type="date"
                  value={lateFilters.toDate}
                  onChange={(e) => setLateFilters((f) => ({ ...f, toDate: e.target.value || f.toDate }))}
                />
              </ReportFilterField>
              {departmentSelect(lateFilters, setLateFilters, "ar-late-dept")}
            </>
          }
          actions={
            <ReportActions
              variant="enterprise"
              busy={busy}
              showPdf={false}
              onView={() => runAction("late-view", loadLateRows, "", "", "Late Coming Report", LATE_COLS)}
              onExcel={() => runAction("late-excel", loadLateRows, "late_coming_report.xlsx", "Late Coming", "")}
            />
          }
        />

        <ReportCard
          layout="enterprise"
          reportType="attendance-register"
          icon={ClipboardList}
          iconTone="blue"
          title="Attendance Register"
          description="Monthly attendance sheet with day-wise status."
          filters={
            <>
              {monthYearSelects(registerFilters, setRegisterFilters, "ar-register")}
              {departmentSelect(registerFilters, setRegisterFilters, "ar-register-dept")}
            </>
          }
          actions={
            <ReportActions
              variant="enterprise"
              busy={busy}
              onView={() =>
                runAction("register-view", loadRegisterFull, "", "", "Attendance Register", registerFullCols)
              }
              onExcel={() =>
                runAction("register-excel", loadRegisterFull, "attendance_register.xlsx", "Attendance Register", "")
              }
              onPdf={() =>
                runAction(
                  "register-pdf",
                  loadRegisterFull,
                  "attendance_register.pdf",
                  "",
                  "Attendance Register",
                  registerFullCols
                )
              }
            />
          }
        />
      </div>

      {viewModal && (
        <ReportViewModal
          title={viewModal.title}
          columns={viewModal.columns}
          rows={viewModal.rows}
          modalId="ar-modal-title"
          headerExtra={viewModal.showLegend ? <RegisterLegend inline /> : null}
          onClose={() => setViewModal(null)}
        />
      )}
    </div>
  );
}
